import { WorkbookOptions } from "@/utilities/xlsx";

export interface GridWorkbookOptionsInput {
  /** The exported fields, in column order — this is what maps a field to a column number. */
  exportedFields: string[];
  /** Field to grid column type, as declared in the column definitions. */
  columnTypes: Record<string, string | undefined>;
  /** Whether the grid put its header in the first row. */
  includeHeaders: boolean;
  /**
   * 1-based sheet row the grid table starts at. Above 1 when the sheet carries a metadata
   * preamble, which shifts the header row down by that much.
   */
  firstRow?: number;
}

/**
 * Works out the workbook formatting for a grid export.
 *
 * Which columns hold numbers is taken from the grid's own column definitions — the same signal
 * MUI's CSV serializer uses — rather than guessed from the exported strings, so a licence plate
 * or a serial number stays text.
 */
export function buildGridWorkbookOptions(input: GridWorkbookOptionsInput): WorkbookOptions {
  const { exportedFields, columnTypes, includeHeaders } = input;
  const firstRow = input.firstRow ?? 1;

  const numericColumns = exportedFields.reduce<number[]>((columns, field, index) => {
    if (columnTypes[field] === "number") {
      columns.push(index + 1);
    }
    return columns;
  }, []);

  return {
    headerRow: includeHeaders ? firstRow : undefined,
    // Stated outright rather than left to follow from `headerRow`: with a preamble and no header
    // row there is nothing for it to follow from, and the preamble would be read as data.
    firstDataRow: includeHeaders ? firstRow + 1 : firstRow,
    numericColumns,
  };
}
